import firebase from 'firebase/app';
import 'firebase/database';
import reporter from './reporter';

class FirebaseClient {
    constructor(config) {
        firebase.initializeApp(config);
        this.db = firebase.database();
        this.sessionName = '';
        this.userName = '';
        this.isRemovedFromSession = false;
        reporter.log('new FirebaseClient()');
    }

    initialize(sessionName, userName, sessionType = 'points') {
        console.info('DB connected');
        this.online();
        // Separate storage per route: poker and t-shirt sessions don't share players
        this.sessionName = sessionType === 'tshirt' ? `tshirt_${sessionName}` : sessionName;
        this.userName = userName;
        this.sessionType = sessionType;
        if (this.userName) {
            const unvotedValue = sessionType === 'tshirt' ? '' : 0;
            this.setPoint(unvotedValue);
            // track online status
            const connectedRef = this.db.ref('.info/connected');
            connectedRef.on('value', (snap) => {
                if (snap.val() === true && !this.isRemovedFromSession) {
                    const con = this.db.ref(this.sessionName + '/players/' + this.userName + '/connected');
                    con.onDisconnect().remove();
                    con.set(true);
                }
            });
        }
    }

    setPoint(point, cheated = false) {
        const data = {
            '/point': point,
            '/connected': true,
            '/cheated': cheated,
        };
        this.db
            .ref(this.sessionName + '/players/' + this.userName)
            .update(data)
            .catch(this.errorHandler);
    }

    clearVotes(mode = 'points') {
        const unvotedValue = mode === 'tshirt' ? '' : 0;
        this.db
            .ref(this.sessionName)
            .once('value')
            .then((snapshot) => {
                const res = snapshot.val();
                const newSessionData = {
                    time: new Date().toISOString(),
                    showPoints: 0,
                    players: {},
                };
                for (const index in res.players) {
                    newSessionData.players[index] = {
                        point: unvotedValue,
                        cheated: false,
                        connected: !!res.players[index].connected,
                    };
                }
                this.db.ref(this.sessionName).set(newSessionData).catch(this.errorHandler);
            })
            .catch(this.errorHandler);
    }

    showVotes() {
        this.db
            .ref(this.sessionName + '/showPoints')
            .set(1)
            .catch(this.errorHandler);
    }

    deletePlayer(userName) {
        if (userName) {
            this.db
                .ref(this.sessionName + '/players/' + userName)
                .remove()
                .catch(this.errorHandler);
        }
    }

    setTshirtLegend(legend) {
        if (this.sessionType === 'tshirt') {
            this.db
                .ref(this.sessionName + '/tshirtLegend')
                .set(legend)
                .catch(this.errorHandler);
        }
    }

    markAsRemoved() {
        this.isRemovedFromSession = true;
        // Turn off connection listener to prevent auto-rejoin
        this.db.ref('.info/connected').off();
    }

    rejoinSession() {
        if (!this.userName || !this.sessionName) return;

        // Reset removed flag
        this.isRemovedFromSession = false;

        // Re-add user with unvoted value
        const unvotedValue = this.sessionType === 'tshirt' ? '' : 0;
        this.setPoint(unvotedValue);

        // Re-establish connection tracking
        const connectedRef = this.db.ref('.info/connected');
        connectedRef.on('value', (snap) => {
            if (snap.val() === true && !this.isRemovedFromSession) {
                const con = this.db.ref(this.sessionName + '/players/' + this.userName + '/connected');
                con.onDisconnect().remove();
                con.set(true);
            }
        });
    }

    renameUser(newName) {
        if (!this.sessionName || !this.userName || !newName || this.userName === newName) return;
        const newNameTrimmed = (newName || '').trim().toLowerCase();
        if (!newNameTrimmed || this.userName === newNameTrimmed) return;
        const oldPath = this.sessionName + '/players/' + this.userName;
        const newPath = this.sessionName + '/players/' + newNameTrimmed;
        this.db
            .ref(oldPath)
            .once('value')
            .then((snapshot) => {
                const data = snapshot.val();
                if (!data) {
                    this.userName = newNameTrimmed;
                    return;
                }
                return this.db
                    .ref(newPath)
                    .set({ ...data, connected: true })
                    .then(() => this.db.ref(oldPath).remove())
                    .then(() => {
                        this.userName = newNameTrimmed;
                        const connectedRef = this.db.ref('.info/connected');
                        connectedRef.on('value', (snap) => {
                            if (snap.val() === true && !this.isRemovedFromSession) {
                                const con = this.db.ref(this.sessionName + '/players/' + this.userName + '/connected');
                                con.onDisconnect().remove();
                                con.set(true);
                            }
                        });
                    });
            })
            .catch(this.errorHandler);
    }

    attachListener(callbackFunc) {
        this.db.ref(this.sessionName).off();
        this.db.ref(this.sessionName).on('value', callbackFunc);
        console.info('Watching session: ' + this.sessionName);
    }

    detachListener() {
        this.db.ref(this.sessionName).off();
        this.db.ref('.info/connected').off();
        console.info('Detach listener: ' + this.sessionName);
    }

    online() {
        this.db.goOnline();
    }

    offline() {
        console.info('DB disconnected');
        this.db.goOffline();
    }

    errorHandler(res) {
        console.error(res);
    }
}

const database = new FirebaseClient({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
});

export default database;
